import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { getMappedTerminalPharmacies } from '../api/cp';

function normalizeRow(row) {
  const mappingId = row?.mappingId ?? row?.mapping_id;
  if (mappingId === undefined || mappingId === null || mappingId === '') return null;
  const latitudeRaw = row?.latitude ?? row?.y_pos ?? row?.lat;
  const longitudeRaw = row?.longitude ?? row?.x_pos ?? row?.lng;
  const latitude = latitudeRaw === null || latitudeRaw === undefined || latitudeRaw === '' ? null : Number(latitudeRaw);
  const longitude = longitudeRaw === null || longitudeRaw === undefined || longitudeRaw === '' ? null : Number(longitudeRaw);
  return {
    mappingId: Number(mappingId),
    pharmacyId: row?.pharmacyId ?? row?.pharmacy_id ?? row?.id,
    institutionCode: String(row?.institutionCode ?? row?.institution_code ?? ''),
    externalCustomerCode: String(row?.externalCustomerCode ?? row?.external_customer_code ?? row?.code ?? ''),
    name: String(row?.name ?? row?.customer_name ?? ''),
    phone: String(row?.phone ?? row?.pharmacy_phone ?? ''),
    address: row?.address ?? '',
    businessNo: String(row?.businessNo ?? row?.business_no ?? ''),
    emdongName: String(row?.emdongName ?? row?.emdong_nm ?? ''),
    representativeName: String(row?.representativeName ?? row?.representative_name ?? ''),
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null,
    isDemo: row?.isDemo === true || row?.is_demo === true,
  };
}

function distanceMeters(a, b) {
  const rad = (value) => value * Math.PI / 180;
  const earth = 6371000;
  const dLat = rad(b.latitude - a.latitude);
  const dLng = rad(b.longitude - a.longitude);
  const x = Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return earth * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function formatBusinessNo(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length === 10 ? digits.replace(/^(\d{3})(\d{2})(\d{5})$/, '$1-$2-$3') : (value || '-');
}

function formatDistance(value) {
  const meters = Number(value);
  if (!Number.isFinite(meters)) return '-';
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}

export default function MappedPharmacyDashboard() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    try { localStorage.removeItem('pharmacyListCache'); } catch (_) {}
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
    setRows([]);
  }, [debouncedQuery]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      page === 1 ? setLoading(true) : setLoadingMore(true);
      setError('');
      try {
        const data = await getMappedTerminalPharmacies({ q: debouncedQuery, page, limit: 50 });
        if (cancelled) return;
        const next = Array.isArray(data?.rows) ? data.rows.map(normalizeRow).filter(Boolean) : [];
        setRows((prev) => page === 1 ? next : [...prev, ...next.filter((row) => !prev.some((item) => item.mappingId === row.mappingId))]);
        setTotal(Number(data?.total) || next.length);
      } catch (e) {
        if (!cancelled) setError(e?.message || '담당 거래처를 불러오지 못했습니다.');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [debouncedQuery, page]);

  const rowsWithCoordinates = useMemo(
    () => rows.filter((row) => row.latitude !== null && row.longitude !== null),
    [rows],
  );

  useEffect(() => {
    if (rowsWithCoordinates.length === 0 || !navigator.geolocation) {
      setUserLocation(null);
      return undefined;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => setUserLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
      () => setUserLocation(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
    return undefined;
  }, [rowsWithCoordinates.length]);

  const rowsWithDistance = useMemo(() => {
    if (!userLocation) return rows;
    return rows.map((row) => (
      row.latitude !== null && row.longitude !== null
        ? { ...row, distance: distanceMeters(userLocation, row) }
        : row
    ));
  }, [rows, userLocation]);

  const nearby = useMemo(() => {
    return rowsWithDistance
      .filter((row) => Number.isFinite(row.distance))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 3);
  }, [rowsWithDistance]);

  const selectForPayment = (pharmacy) => {
    navigate('/app/payment', {
      state: {
        from: '/app/dashboard',
        pharmacy,
      },
    });
  };

  return (
    <div style={styles.page}>
      {nearby.length > 0 && !debouncedQuery && (
        <section style={styles.nearbySection}>
          <h2 style={styles.heading}>근처 거래처</h2>
          {nearby.map((row) => (
            <PharmacyRow key={'nearby-' + row.mappingId} row={row} onPay={selectForPayment} highlighted />
          ))}
        </section>
      )}

      <section style={styles.allSection}>
        <div style={styles.search}>
          <Search size={18} color="#8b95a1" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="거래처명 검색"
            style={styles.searchInput}
          />
        </div>
        <div style={styles.list}>
          {loading && <p style={styles.empty}>담당 거래처를 불러오는 중입니다.</p>}
          {!loading && error && <p style={{ ...styles.empty, color: '#e5484d' }}>{error}</p>}
          {!loading && !error && rows.length === 0 && <p style={styles.empty}>담당자에게 매칭된 약국이 없습니다.</p>}
          {!loading && rowsWithDistance.map((row) => <PharmacyRow key={row.mappingId} row={row} onPay={selectForPayment} />)}
          {!loading && rows.length < total && (
            <button type="button" disabled={loadingMore} onClick={() => setPage((v) => v + 1)} style={styles.moreButton}>
              {loadingMore ? '불러오는 중...' : '더 보기 (' + rows.length + '/' + total + ')'}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

function PharmacyRow({ row, onPay, highlighted = false }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ ...styles.row, ...(highlighted ? styles.highlightedRow : {}) }}>
      <div style={styles.rowMain}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={styles.rowTitleWrap}><strong style={styles.rowTitle}>{row.name || '-'}</strong>{row.isDemo && <span style={styles.demoBadge}>시연 데이터</span>}</div>
        </div>
        <div style={styles.rowActions}>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
            style={styles.detailButton}
          >
            상세 {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
          <button type="button" onClick={() => onPay(row)} style={styles.payButton}>결제</button>
        </div>
      </div>
      {expanded && (
        <div style={styles.details}>
          <Detail label="거래처코드" value={row.externalCustomerCode || row.institutionCode || '-'} />
          <Detail label="사업자번호" value={formatBusinessNo(row.businessNo)} />
          <Detail label="읍면동" value={row.emdongName || '-'} />
          <Detail label="거리" value={formatDistance(row.distance)} />
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div style={styles.detailItem}>
      <span style={styles.detailLabel}>{label}</span>
      <strong style={styles.detailValue}>{value}</strong>
    </div>
  );
}

const styles = {
  page: { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#f6f8f7' },
  nearbySection: { flexShrink: 0, padding: '14px 16px 8px' },
  allSection: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '10px 16px 16px' },
  heading: { margin: '0 0 10px', color: '#191f28', fontFamily: "'Pretendard', sans-serif", fontSize: 16, fontWeight: 700, lineHeight: 1.4, letterSpacing: '-0.01em' },
  search: { flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e5e8eb', borderRadius: 12, padding: '0 12px', marginBottom: 10 },
  searchInput: { flex: 1, border: 0, outline: 0, padding: '12px 0', fontSize: 14, minWidth: 0 },
  list: { flex: 1, overflowY: 'auto', background: '#fff', borderRadius: 12, padding: '0 12px' },
  row: { padding: '12px 0', borderBottom: '1px solid #f0f2f1' },
  rowMain: { display: 'flex', alignItems: 'center', gap: 10 },
  rowActions: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 },
  highlightedRow: { background: '#eff8f2', borderRadius: 10, borderBottom: 0, padding: 12, marginBottom: 6 },
  rowTitleWrap: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 },
  demoBadge: { flexShrink: 0, borderRadius: 999, padding: '2px 7px', background: '#f5f3ff', color: '#6d28d9', fontSize: 10, fontWeight: 750 },
  rowTitle: { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#191f28', fontSize: 16 },
  detailButton: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexShrink: 0, border: '1px solid #dfe3e8', background: '#fff', color: '#4e5968', borderRadius: 8, padding: '6px 8px', fontSize: 14, fontWeight: 700 },
  payButton: { flexShrink: 0, border: 0, background: 'var(--brand-primary)', color: '#fff', borderRadius: 8, padding: '7px 12px', fontSize: 16, fontWeight: 700 },
  details: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 14px', marginTop: 11, paddingTop: 11, borderTop: '1px solid #e5e8eb' },
  detailItem: { minWidth: 0 },
  detailLabel: { display: 'block', marginBottom: 3, color: '#8b95a1', fontSize: 11, fontWeight: 600 },
  detailValue: { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#4e5968', fontSize: 13, fontWeight: 650 },
  moreButton: { width: '100%', border: 0, background: '#f2f4f6', borderRadius: 10, padding: 11, margin: '12px 0', color: '#4e5968', fontWeight: 650 },
  empty: { textAlign: 'center', color: '#8b95a1', padding: '42px 8px' },
};
